import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

interface SavedRow {
  id: string;
  destination_id: string;
  created_at: string;
  destinations: {
    id: string;
    slug: string;
    name: string;
    hero_image_url: string | null;
    summary: string | null;
    countries: { name: string } | null;
  } | null;
}

export const useSavedDestinations = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['saved-destinations', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_saved_destinations')
        .select(
          `id, destination_id, created_at,
           destinations:destinations!inner(id, slug, name, hero_image_url, summary, countries:countries(name))`
        )
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as SavedRow[];
    },
  });
};

export const useIsSaved = (destinationId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['is-saved', user?.id, destinationId],
    enabled: !!user && !!destinationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_saved_destinations')
        .select('id')
        .eq('user_id', user!.id)
        .eq('destination_id', destinationId!)
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
  });
};

export const useToggleSavedDestination = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ destinationId, saved }: { destinationId: string; saved: boolean }) => {
      if (!user) throw new Error('You need to sign in first.');
      if (saved) {
        const { error } = await supabase
          .from('user_saved_destinations')
          .delete()
          .eq('user_id', user.id)
          .eq('destination_id', destinationId);
        if (error) throw error;
        return { saved: false };
      } else {
        const { error } = await supabase
          .from('user_saved_destinations')
          .insert({ user_id: user.id, destination_id: destinationId });
        if (error) throw error;
        return { saved: true };
      }
    },
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: ['is-saved', user?.id, vars.destinationId] });
      qc.invalidateQueries({ queryKey: ['saved-destinations', user?.id] });
      toast({
        title: res.saved ? 'Saved' : 'Removed',
        description: res.saved ? 'Added to your trips.' : 'Removed from your trips.',
      });
    },
    onError: (err: Error) => {
      toast({ title: 'Something went wrong', description: err.message, variant: 'destructive' });
    },
  });
};
